import type { Reference } from "../../../lib/proto/gen/typescript/agency/reference/v1/reference";

// Resolver maps a psref:/// Reference to its external public URI.
// Injected by the application layer — never provided by this lib.
export type ReferenceResolver = (ref: Reference) => string | Promise<string>;

// Canonicalizer maps an inbound public URI to a Reference (psref form).
// Returns null if the URI is not a known PineSeed resource — not an error, just not applicable.
// Injected at the app boundary — never provided by this lib.
export type ReferenceCanonicalizer = (uri: string) => Reference | null | Promise<Reference | null>;

export const RenderFormat = {
  MARKDOWN_LINK: "markdown-link",
  MARKDOWN_IMAGE: "markdown-image",
  HTML_LINK: "html-link",
  HTML_IMAGE: "html-image",
} as const;

export type RenderFormat = (typeof RenderFormat)[keyof typeof RenderFormat];

export interface RenderOptions {
  format: RenderFormat;
  // resolver is required when the reference URI is a psref:/// — it maps it to a public URL.
  // For non-psref URIs, resolver is not called.
  resolver?: ReferenceResolver;
}

// renderReference renders a single Reference to a string in the specified format.
// Throws if a psref:/// URI is present and no resolver is provided.
// Throws if the reference is missing required display fields for the chosen format.
export async function renderReference(
  ref: Reference,
  options: RenderOptions
): Promise<string> {
  if (!ref.uri) {
    throw new Error("reference: cannot render reference with empty uri");
  }

  let resolvedUri: string;

  if (ref.psrefUri && ref.psrefUri.startsWith("psref:")) {
    if (!options.resolver) {
      throw new Error(
        `reference: resolver is required to render psref:/// URI "${ref.psrefUri}"`
      );
    }
    resolvedUri = await options.resolver(ref);
    if (!resolvedUri) {
      throw new Error(`reference: resolver returned empty URI for reference id "${ref.id}"`);
    }
  } else {
    resolvedUri = ref.uri;
  }

  switch (options.format) {
    case RenderFormat.MARKDOWN_LINK:
      return renderMarkdownLink(ref, resolvedUri);
    case RenderFormat.MARKDOWN_IMAGE:
      return renderMarkdownImage(ref, resolvedUri);
    case RenderFormat.HTML_LINK:
      return renderHtmlLink(ref, resolvedUri);
    case RenderFormat.HTML_IMAGE:
      return renderHtmlImage(ref, resolvedUri);
    default:
      throw new Error(`reference: unsupported render format "${options.format}"`);
  }
}

// renderReferences replaces all ParsedReference occurrences in text with their rendered forms.
// Uses parseReferences output; positions are applied right-to-left to preserve indices.
export async function renderReferences(
  text: string,
  references: Array<{ reference: Reference; start: number; end: number; isImage: boolean }>,
  resolver?: ReferenceResolver
): Promise<string> {
  // Process right-to-left so earlier indices stay valid after substitution.
  const sorted = [...references].sort((a, b) => b.start - a.start);

  let result = text;
  for (const { reference, start, end, isImage } of sorted) {
    const format = isImage ? RenderFormat.MARKDOWN_IMAGE : RenderFormat.MARKDOWN_LINK;
    const opts: RenderOptions = resolver ? { format, resolver } : { format };
    const rendered = await renderReference(reference, opts);
    result = result.slice(0, start) + rendered + result.slice(end);
  }

  return result;
}

// Matches plain markdown links and images that are NOT already psref references.
// Capture groups: 1=image flag ("!" or ""), 2=label, 3=uri, 4=description
// Skips [@...] — those are already psref references.
export const PLAIN_LINK_PATTERN = (): RegExp =>
  /(!?)\[([^@\]][^\]]*|[^@\]]?)\]\(([^)]+?)(?:\s+"([^"]*)")?\)/g;

// canonicalizeReferences scans text for plain markdown links, calls the canonicalizer
// for each URI, and replaces matching links with their canonical psref form.
// Links the canonicalizer returns null for are left as-is.
export async function canonicalizeReferences(
  text: string,
  canonicalizer: ReferenceCanonicalizer,
): Promise<string> {
  const pattern = PLAIN_LINK_PATTERN();
  const substitutions: Array<{ start: number; end: number; rendered: string }> = [];
  let m: RegExpExecArray | null;

  while ((m = pattern.exec(text)) !== null) {
    const [raw, imageFlag, , uri] = m;
    if (!uri) continue;
    const ref = await canonicalizer(uri);
    if (!ref) continue;

    // Preserve title and description from the original link if not set on the ref.
    const mergedRef: Reference = {
      ...ref,
      title: ref.title,
      description: ref.description,
    };

    const psref = mergedRef.psrefUri || mergedRef.uri;
    const rendered = imageFlag === "!"
      ? renderMarkdownImage(mergedRef, psref)
      : renderMarkdownLink(mergedRef, psref);
    substitutions.push({ start: m.index, end: m.index + raw.length, rendered });
  }

  let result = text;
  for (const { start, end, rendered } of substitutions.sort((a, b) => b.start - a.start)) {
    result = result.slice(0, start) + rendered + result.slice(end);
  }
  return result;
}

function renderMarkdownLink(ref: Reference, uri: string): string {
  const label = ref.title;
  const title = ref.description ? ` "${ref.description}"` : "";
  return `[@${label}](${uri}${title})`;
}

function renderMarkdownImage(ref: Reference, uri: string): string {
  const alt = ref.title;
  const title = ref.description ? ` "${ref.description}"` : "";
  return `![@${alt}](${uri}${title})`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderHtmlLink(ref: Reference, uri: string): string {
  const label = `@${ref.title}`;
  const title = ref.description ? ` title="${escapeHtml(ref.description)}"` : "";
  return `<a href="${escapeHtml(uri)}"${title}>${escapeHtml(label)}</a>`;
}

function renderHtmlImage(ref: Reference, uri: string): string {
  const alt = `@${ref.title}`;
  const title = ref.description ? ` title="${escapeHtml(ref.description)}"` : "";
  return `<img src="${escapeHtml(uri)}" alt="${escapeHtml(alt)}"${title} />`;
}
